import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { UserService } from './user.service';
import { environment } from '../../environments/environment';
import { WorkspaceInterface, WorkspaceInvitation, WorkspaceVirtualAgent } from 'voxr-shared';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userService = inject(UserService);

  private _workspaces$ = new ReplaySubject<WorkspaceInterface[] | undefined>(1);
  workspaces$ = this._workspaces$.asObservable();
  private _workspace$ = new ReplaySubject<WorkspaceInterface | undefined>(1);
  workspace$ = this._workspace$.asObservable();
  workspaceId?: string;

  constructor() {
    this.userService.user$.subscribe(async (user) => {
      if (user == undefined) return;
      if (!user) {
        this._workspaces$.next(undefined);
        this._workspace$.next(undefined);
      }
      else await this.loadWorkspace();
    });
  }

  async loadWorkspace(workspaceId?: string) {
    const workspaces = await this.list();
    this._workspaces$.next(workspaces);
    if (workspaceId) {
      const workspace = workspaces.find(w => w._id == workspaceId);
      if (workspace) this._workspace$.next(workspace);
      this.workspaceId = workspace?._id!.toString();
      return !!workspace;
    }
    return false;
  }

  async current() {
    const workspace = await firstValueFrom(this.http.get<WorkspaceInterface>(`${environment.app.serviceUrl}/workspace/current`));
    this._workspace$.next(workspace);
    return workspace;
  }

  async list() {
    return firstValueFrom(this.http.get<WorkspaceInterface[]>(`${environment.app.serviceUrl}/workspace/list`));
  }

  async setWorkspace(id: string) {
    await this.router.navigate(['/w', id]);
    window.location.reload();
  }

  async save(workspace: Partial<WorkspaceInterface>) {
    await firstValueFrom(this.http.post<WorkspaceInterface>(`${environment.app.serviceUrl}/workspace/save`, workspace));
    await this.current();
  }

  async create(workspace: Partial<WorkspaceInterface>, inviteAdmin?: string) {
    const result = await firstValueFrom(this.http.post<WorkspaceInterface>(`${environment.app.serviceUrl}/workspace/create`, { workspace, inviteAdmin }));
    this._workspaces$.next(await this.list());
    return result;
  }

  private _members: any[] = [];
  async listMembers(workspaceId?: string) {
    if(workspaceId) 
      return firstValueFrom(this.http.get<any[]>(`${environment.app.serviceUrl}/workspace/members/${workspaceId}`));

    if (!this._members?.length) {
      this._members = await firstValueFrom(this.http.get<any[]>(`${environment.app.serviceUrl}/workspace/members`));
    }
    return this._members;
  }

  async getInvitation(workspaceId: string, invitationId: string) {
    return firstValueFrom(this.http.get<{ invitation: any, workspaceName: string }>(`${environment.app.serviceUrl}/workspace/invitation/${workspaceId}/${invitationId}`));
  }

  async invite(emails: string[], roles?: string[]) {
    const res = await firstValueFrom(this.http.post<WorkspaceInvitation[]>(`${environment.app.serviceUrl}/workspace/invite`, { emails, roles }));
    this._members = [];
    return res;
  }

  async acceptInvitation(workspaceId: string) {
    const res = await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/invitation/accept`, { workspaceId }));
    this._members = [];
    return res;
  }

  async revokeInvitation(invitationId: string) {
    const res = await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/invitation/revoke`, { invitationId }));
    this._members = [];
    return res;
  }

  async resendInvitation(invitationId: string) {
    const res = await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/invitation/resend`, { invitationId }));
    this._members = [];
    return res;
  }

  async saveVirtualAgent(agent: WorkspaceVirtualAgent) {
    await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/virtual-agent`, { agent }));
    await this.current();
  }
  async removeVirtualAgent(agentId: string) {
    await firstValueFrom(this.http.delete(`${environment.app.serviceUrl}/workspace/virtual-agent/${agentId}`));
    await this.current();
  }

  async saveMember(member: any) {
    const res = await firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/member/save`, { member }));
    this._members = [];
    return res;
  }

  async removeMember(memberId: string) {
    const res = firstValueFrom(this.http.post(`${environment.app.serviceUrl}/workspace/member/remove`, { memberId }));
    this._members = [];
    return res;
  }
}
